import { cn } from '@cobliteam/ui/utils';
import type { ComponentProps } from 'react';
import remarkGfm from 'remark-gfm';
import { Streamdown } from 'streamdown';

export type AIChatMarkdownVariant = 'chat' | 'assistant';

export type AIChatMarkdownComponents = NonNullable<ComponentProps<typeof Streamdown>['components']>;

interface AIChatMarkdownProps {
  children: string;
  components?: AIChatMarkdownComponents;
  isAnimating?: boolean;
  mode?: ComponentProps<typeof Streamdown>['mode'];
  variant?: AIChatMarkdownVariant;
}

const markdownSizeClasses: Record<AIChatMarkdownVariant, Record<string, string>> = {
  chat: {
    h1: 'mb-2 mt-4 text-xl font-bold leading-8 tracking-tight first:mt-0',
    h2: 'mb-2 mt-4 text-lg font-semibold leading-7 tracking-tight first:mt-0',
    h3: 'mb-1.5 mt-3 text-base font-semibold leading-6 first:mt-0',
    h4: 'mb-1.5 mt-3 text-sm font-semibold leading-6 first:mt-0',
    h5: 'mb-1 mt-3 text-sm font-medium leading-5 first:mt-0',
    h6: 'mb-1 mt-2 text-xs font-medium uppercase tracking-[0.08em] first:mt-0',
    paragraph: 'my-2 first:mt-0 last:mb-0',
    list: 'my-2 list-none text-gray-11',
    orderedList: 'list-none text-gray-11 hover:text-gray-11',
    listItem: 'my-1.5 hover:text-gray-11',
    blockquote: 'my-4 border-gray-5 border-l-2 pl-4 text-gray-11 italic',
    code: 'rounded bg-gray-3 px-1.5 py-0.5 font-mono text-[0.95em] text-gray-12',
    pre: 'my-4 overflow-x-auto rounded-lg border border-gray-5 bg-gray-2 p-3 text-sm',
    table: 'text-sm',
    tableWrapper: 'my-4 overflow-hidden rounded-lg border border-gray-6',
    tableHead: 'bg-gray-2 text-gray-12',
    headerCell: 'border-gray-6 border-r border-b px-4 py-2.5 text-left font-semibold last:border-r-0',
    cell: 'border-gray-4 border-r border-b px-4 py-2 text-gray-11 last:border-r-0',
    link: 'font-medium text-link underline-offset-2 hover:text-link hover:underline',
    strong: 'font-semibold text-gray-12',
    emphasis: 'italic text-gray-12',
    divider: 'border-gray-4',
    ul: 'my-3 list-none text-[13px] text-gray-11 leading-6',
    ol: 'my-3 list-none text-[13px] text-gray-11 leading-6',
    li: 'my-2 text-[13px] text-gray-11 leading-6 hover:text-gray-11',
    br: 'my-10',
  },
  assistant: {
    h1: 'mb-4 mt-8 text-[18px] font-bold text-gray-12 leading-7 tracking-tight first:mt-0',
    h2: 'mb-3 mt-7 text-[16px] font-semibold text-gray-12 leading-7 tracking-tight first:mt-0',
    h3: 'mb-3 mt-6 text-[15px] font-semibold text-gray-12 leading-6 first:mt-0',
    h4: 'mb-2.5 mt-5 text-[14px] font-semibold text-gray-12 leading-6 first:mt-0',
    h5: 'mb-2 mt-4 text-[13px] font-semibold text-gray-11 leading-5 first:mt-0',
    h6: 'mb-2 mt-4 text-[11px] font-semibold text-gray-10 uppercase leading-4 tracking-[0.1em] first:mt-0',
    paragraph: 'my-3 first:mt-0 last:mb-0 text-[13px] text-gray-11 leading-6',
    list: 'my-3 list-none text-[13px] text-gray-11 leading-6',
    orderedList: 'list-none text-[13px] text-gray-11 leading-6 hover:text-gray-11',
    listItem: 'my-2 text-[13px] text-gray-11 leading-6 hover:text-gray-11',
    blockquote: 'my-5 rounded-r-xl border-l-[3px] border-primary-7 bg-primary-2/40 px-4 py-3 text-[13px] text-gray-11 italic leading-6',
    code: 'rounded-md bg-gray-3 px-1.5 py-0.5 font-mono text-[12px] text-gray-12',
    pre: 'my-5 overflow-x-auto rounded-xl border border-gray-5 bg-[#f5f7fa] p-3 text-[12px] leading-5 shadow-[inset_0_1px_0_rgba(255,255,255,0.5)]',
    table: 'text-[12px]',
    tableWrapper: 'my-5 overflow-hidden rounded-xl border border-gray-5 bg-white shadow-[0_1px_0_rgba(15,23,42,0.03)]',
    tableHead: 'bg-gray-2/70 text-gray-12',
    headerCell: 'border-gray-6 border-r border-b px-3 py-2 text-[11px] text-left font-semibold last:border-r-0',
    cell: 'border-gray-4 border-r border-b px-3 py-1.5 text-[12px] text-gray-11 last:border-r-0',
    link: 'font-medium text-primary-10 underline decoration-primary-6/50 underline-offset-2 hover:text-primary-11',
    strong: 'font-semibold text-gray-12',
    emphasis: 'italic text-gray-12',
    ul: 'my-3 list-none text-[13px] text-gray-11 leading-6',
    ol: 'my-3 list-none text-[13px] text-gray-11 leading-6',
    li: 'my-2 text-[13px] text-gray-11 leading-6 hover:text-gray-11',
    divider: 'my-10 border-gray-5',
    br: 'my-10',
  },
};

const AIChatMarkdown = ({ children, isAnimating = false, mode = 'streaming', variant = 'chat', components }: AIChatMarkdownProps) => {
  const sizeClasses = markdownSizeClasses[variant];

  const defaultComponents: AIChatMarkdownComponents = {
    h1: (props) => <h1 {...(props as ComponentProps<'h1'>)} className={sizeClasses.h1} />,
    h2: (props) => <h2 {...(props as ComponentProps<'h2'>)} className={sizeClasses.h2} />,
    h3: (props) => <h3 {...(props as ComponentProps<'h3'>)} className={sizeClasses.h3} />,
    h4: (props) => <h4 {...(props as ComponentProps<'h4'>)} className={sizeClasses.h4} />,
    h5: (props) => <h5 {...(props as ComponentProps<'h5'>)} className={sizeClasses.h5} />,
    h6: (props) => <h6 {...(props as ComponentProps<'h6'>)} className={sizeClasses.h6} />,
    hr: () => <hr className={sizeClasses.divider} />,
    br: () => <br className={sizeClasses.br} />,
    p: (props) => <p {...(props as ComponentProps<'p'>)} className={sizeClasses.paragraph} />,
    ol: (props) => <ol {...(props as ComponentProps<'ol'>)} className={sizeClasses.orderedList} />,
    ul: (props) => <ul {...(props as ComponentProps<'ul'>)} className={sizeClasses.list} />,
    li: (props) => <li {...(props as ComponentProps<'li'>)} className={cn(sizeClasses.listItem)} />,
    blockquote: (props) => <blockquote {...(props as ComponentProps<'blockquote'>)} className={sizeClasses.blockquote} />,
    code: (props) => <code {...(props as ComponentProps<'code'>)} className={sizeClasses.code} />,
    pre: (props) => <pre {...(props as ComponentProps<'pre'>)} className={sizeClasses.pre} />,
    table: ({ children: tableChildren }) => (
      <div className={sizeClasses.tableWrapper}>
        <table className={cn('w-full border-collapse', sizeClasses.table)}>{tableChildren}</table>
      </div>
    ),
    thead: (props) => <thead {...(props as ComponentProps<'thead'>)} className={sizeClasses.tableHead} />,
    th: (props) => <th {...(props as ComponentProps<'th'>)} className={sizeClasses.headerCell} />,
    td: (props) => <td {...(props as ComponentProps<'td'>)} className={sizeClasses.cell} />,
    tr: (props) => <tr {...(props as ComponentProps<'tr'>)} className='transition-colors last:border-b-0 hover:bg-gray-1' />,
    a: (props) => <a {...(props as ComponentProps<'a'>)} className={sizeClasses.link} />,
    strong: (props) => <strong {...(props as ComponentProps<'strong'>)} className={sizeClasses.strong} />,
    em: (props) => <em {...(props as ComponentProps<'em'>)} className={sizeClasses.emphasis} />,
  };

  return (
    <Streamdown linkSafety={{ enabled: false }} mode={mode} isAnimating={isAnimating} remarkPlugins={[remarkGfm]} components={{ ...defaultComponents, ...components }}>
      {children}
    </Streamdown>
  );
};

export default AIChatMarkdown;
